































































































































































































































// CopyToClipboardButton is used only by the commented-out copy asset ID button
// import CopyToClipboardButton from '@/app/components/CopyToClipboardButton.vue';
// imported directly (not from '@/app/components'): this component is loaded through the asset
// components barrel in main.ts before Vue.use(VueCompositionAPI) runs, and the barrel would pull
// in composables that call ref() at module scope
import DropdownMenu from '@/app/components/DropdownMenu.vue';
import { useFeatureFlags, useFilters } from '@/app/composable';
import { AccessLevelsOptions } from '@/modules/access-policy/constants/access-levels.constants';
import store from '@/app/store';
import {
    // ArchiveIcon, // used only by the commented-out quick action icons
    // BellIcon, // used only by the commented-out quick action icons
    // ClockIcon, // used only by the commented-out "Updated on" label
    // CloudDownloadIcon, // used only by the commented-out quick action icons
    // LinkIcon, // used only by the commented-out quick action icons
    // MinusIcon, // used only by the commented-out quick action icons
    // PencilAltIcon, // used only by the commented-out quick action icons
    // ReplyIcon, // used only by the commented-out quick action icons
    // TrashIcon, // used only by the commented-out quick action icons
} from '@vue-hero-icons/outline';
import { CalendarIcon, DotsVerticalIcon, UserIcon } from '@vue-hero-icons/solid';
import { PropType, Ref, computed, defineComponent } from '@vue/composition-api';
import dayjs from 'dayjs';
import minMax from 'dayjs/plugin/minMax';
import utc from 'dayjs/plugin/utc';
import * as R from 'ramda';
import { useAssetRetrieval } from '../composable/asset-retrieval';
import { useAssetStatus } from '../composable/asset-status';
import { AssetType, AssetTypeId, StatusCode } from '../constants';
import { Asset } from '../types';
import AssetTypeIcon from './AssetTypeIcon.vue';
dayjs.extend(utc);
dayjs.extend(minMax);

export default defineComponent({
    name: 'AssetOverview',
    components: {
        // BellIcon, // used only by the commented-out quick action icons
        // TrashIcon, // used only by the commented-out quick action icons
        // PencilAltIcon, // used only by the commented-out quick action icons
        // LinkIcon, // used only by the commented-out quick action icons
        // ArchiveIcon, // used only by the commented-out quick action icons
        // ReplyIcon, // used only by the commented-out quick action icons
        UserIcon,
        // MinusIcon, // used only by the commented-out quick action icons
        // CloudDownloadIcon, // used only by the commented-out quick action icons
        CalendarIcon,
        // ClockIcon, // used only by the commented-out "Updated on" label
        DotsVerticalIcon,
        AssetTypeIcon,
        // CopyToClipboardButton, // used only by the commented-out copy asset ID button
        DropdownMenu,
    },
    props: {
        asset: {
            type: Object as PropType<Asset>,
            required: true,
        },
        filter: {
            type: String,
            required: true,
        },
        alert: {
            type: Object as PropType<{ entityId: string | number; alertId: string; enabled: boolean }>,
            required: false,
        },
    },
    setup(props, { emit, root }) {
        const { flag } = useFeatureFlags();
        const isAlertsEnabled = flag('alerts');

        const user = computed(() => store.state.auth.user);
        const isAssetCreator = computed(() => user.value && user.value.id === props.asset.createdBy.id);
        const updatedDate = computed(() =>
            props.asset.modifiedAt
                ? dayjs.max([dayjs(props.asset.modifiedAt), dayjs(props.asset.updatedAt)]).toString()
                : props.asset.updatedAt,
        );

        const { formatDateTime } = useFilters();

        const assetType = computed(() => {
            switch (props.asset.assetTypeId) {
                case AssetTypeId.Result:
                    return {
                        type: AssetType.Result,
                        class: 'text-blue-800',
                    };
                default:
                    return {
                        type: AssetType.Dataset,
                        class: 'text-orange-800',
                    };
            }
        });

        const accessibilityLabels: Record<string, string> = {
            'Through an API': 'API',
            'Through a data streaming mechanism': 'Stream',
            'As a downloadable file': 'File',
        };

        // The assets list is served by the search service, and its documents carry only a projection of the
        // asset's metadata: `general.tags` and `distribution.accessibility`. Extent (temporal/spatial
        // resolution and coverage) and `distribution.format` are NOT indexed, so those fields stay empty on
        // the card even when they are filled in and shown on the asset's page - they would need to be added
        // to the search index first. They are read here anyway so that they appear as soon as that happens.
        const metadataField = (group: 'extent' | 'distribution', field: string) =>
            R.path(['metadata', group, field], props.asset as any);

        // Same formatting as the asset's detail page (ViewAsset), so the card and the page agree.
        const temporalCoverage = computed(() => {
            const coverage: any = metadataField('extent', 'temporalCoverage');
            if (!coverage?.unit) return null;
            switch (coverage.unit) {
                case 'Time Period':
                    return `${dayjs(coverage.min).format('MMM D, YYYY')} ~ ${dayjs(coverage.max).format('MMM D, YYYY')}`;
                case 'Single Date':
                    return dayjs(coverage.min).format('MMM D, YYYY');
                case 'Calculated based on data': {
                    if (!coverage.min || !coverage.max || !coverage.field) return null;
                    switch (coverage.field.type) {
                        case 'date':
                            return `${dayjs(coverage.min).format('MMM D, YYYY')} ~ ${dayjs(coverage.max).format(
                                'MMM D, YYYY',
                            )}`;
                        case 'time':
                            return `${dayjs(`1970-01-01T${coverage.min}`).utc().format('HH:mm:ss UTC')} ~ ${dayjs(
                                `1970-01-01T${coverage.max}`,
                            )
                                .utc()
                                .format('HH:mm:ss UTC')}`;
                        case 'datetime':
                            return `${dayjs(coverage.min).utc().format('MMM D, YYYY HH:mm:ss UTC')} ~ ${dayjs(
                                coverage.max,
                            )
                                .utc()
                                .format('MMM D, YYYY HH:mm:ss UTC')}`;
                        default:
                            return null;
                    }
                }
                case 'Not applicable':
                    return coverage.unit;
                default:
                    return `${coverage.value} ${coverage.unit}`;
            }
        });

        // The metadata shown in the panel on the card. Fields that have not been filled in are left out
        // altogether, so the panel only ever shows real values (and disappears when there are none).
        const assetDetails = computed(() => {
            const details: { label: string; value: string; tooltip: string; testId: string }[] = [];
            const asset = props.asset as any;
            const temporalResolution: any = metadataField('extent', 'temporalResolution');
            const spatialResolution: any = metadataField('extent', 'spatialResolution');
            const format: any = metadataField('distribution', 'format');
            const accessibility: any = metadataField('distribution', 'accessibility');
            const { volume } = asset;

            if (temporalResolution?.unit && temporalResolution.unit !== 'Not applicable')
                details.push({
                    label: 'Resolution',
                    value: temporalResolution.value
                        ? `${temporalResolution.value} ${temporalResolution.unit}`
                        : temporalResolution.unit,
                    tooltip:
                        'Temporal resolution: the frequency of acquiring new data from the same data source (e.g. as part of a dynamic process from a system/location/sensor).',
                    testId: 'asset-temporal-resolution',
                });

            if (spatialResolution?.unit && spatialResolution.unit !== 'Not applicable')
                details.push({
                    label: 'Granularity',
                    value: spatialResolution.unit,
                    tooltip:
                        'Spatial resolution: the granularity applied within the data allowing to distinguish different spaces using the data.',
                    testId: 'asset-spatial-resolution',
                });

            if (format?.length)
                details.push({
                    label: 'Format',
                    value: format.join(', '),
                    tooltip: `Format: ${format.join(', ')}`,
                    testId: 'asset-format',
                });

            if (temporalCoverage.value)
                details.push({
                    label: 'Coverage',
                    value: temporalCoverage.value,
                    tooltip: `Temporal coverage: ${temporalCoverage.value}`,
                    testId: 'asset-temporal-coverage',
                });

            if (volume && !R.isNil(volume.value) && !R.isEmpty(volume.value))
                details.push({
                    label: 'Volume',
                    value: `${volume.value} ${volume.value === 1 ? volume.unit.slice(0, -1) : volume.unit}`,
                    tooltip: `Volume: ${volume.value} ${volume.value === 1 ? volume.unit.slice(0, -1) : volume.unit}`,
                    testId: 'asset-volume',
                });

            if (accessibility?.length)
                details.push({
                    label: 'Accessibility',
                    // shortened for the card; the full wording is kept in the tooltip
                    value: accessibility.map((option: string) => accessibilityLabels[option] ?? option).join(', '),
                    tooltip: `Accessibility: ${accessibility.join(', ')}`,
                    testId: 'asset-accessibility',
                });

            if (asset.accessLevel)
                details.push({
                    label: 'Access',
                    value: (AccessLevelsOptions as Record<string, string>)[asset.accessLevel] ?? asset.accessLevel,
                    tooltip: 'The access level of the asset, i.e. who else can see and retrieve it.',
                    testId: 'asset-access-level',
                });

            return details;
        });

        const allFilterEnabled = computed(() => props.filter === 'all');

        const status = computed(() => props.asset?.status);
        const asset: Ref<Asset> = computed<Asset>(() => props.asset);
        const { label: assetStatusLabel, colour: statusClass } = useAssetStatus(status);
        const { isRetrievalDisabled, retrievalTooltip, accessibilityKey, isRetrievalAllowed } = useAssetRetrieval(
            asset,
            user,
        );

        const provenanceLabel = computed(() =>
            props.asset.assetTypeId === AssetTypeId.Dataset
                ? 'Navigate to Ingestion Job'
                : 'Navigate to Analytics Pipeline',
        );

        const provenanceLinkTooltip = computed(() => {
            const pipelineType =
                props.asset.assetTypeId === AssetTypeId.Dataset ? 'Ingestion Job' : 'Analytics Pipeline';
            if (props.asset.provenanceAssetWorkflowIds?.length === 0)
                return `The ${pipelineType}, which created this asset, has been deleted.`;

            return `Navigate to ${pipelineType}`;
        });

        const retrieve = () => {
            if (accessibilityKey.value)
                root.$router.push({
                    name: 'retrieval:create',
                    params: { assetId: `${props.asset.id}`, accessibility: accessibilityKey.value, backTo: 'assets' },
                });
        };

        const editAlert = () => {
            if (props.alert)
                root.$router.push({ name: 'alerts:edit', params: { id: props.alert.alertId, backTo: 'assets' } });
        };

        const actionOptions = computed(() => {
            const options: { name: string; action: () => void }[] = [];
            if (status.value !== StatusCode.Deleted && isAssetCreator.value)
                options.push({ name: 'Edit', action: () => emit('edit', props.asset) });
            // The "Retrieve Data" action is no longer displayed.
            // Kept commented out in case it needs to be restored.
            // if (isRetrievalAllowed.value && !isRetrievalDisabled.value)
            //     options.push({ name: 'Retrieve Data', action: () => retrieve() });
            if (status.value !== StatusCode.Deleted && isAlertsEnabled.value && props.alert)
                options.push({ name: 'Navigate to Alert', action: () => editAlert() });
            (props.asset.provenanceAssetWorkflowIds || []).forEach((provenanceAssetWorkflowId: string, idx: number) => {
                options.push({
                    name: `${provenanceLabel.value}${
                        (props.asset.provenanceAssetWorkflowIds || []).length > 1 ? ` ${idx + 1}` : ''
                    }`,
                    action: () => emit('navigate', provenanceAssetWorkflowId, props.asset.assetTypeId),
                });
            });
            if (status.value !== StatusCode.Deleted && isAssetCreator.value)
                options.push({ name: 'Delete', action: () => emit('destroy', props.asset) });
            return options;
        });

        return {
            dayjs,
            emit,
            statusClass,
            isAlertsEnabled,
            assetStatusLabel,
            user,
            StatusCode,
            status,
            isAssetCreator,
            allFilterEnabled,
            assetDetails,
            AssetType,
            assetType,
            provenanceLinkTooltip,
            provenanceLabel,
            R,
            retrieve,
            retrievalTooltip,
            isRetrievalDisabled,
            isRetrievalAllowed,
            formatDateTime,
            updatedDate,
            accessibilityKey,
            editAlert,
            actionOptions,
        };
    },
});
